// in this component i will accept media url that could be video or image, i need to crop and intrun need to cropx and cropy

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, message, Spin } from 'antd';
import { Cropper } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import './cropMedia.less';

interface CropMediaProps {
  mediaUrl: string;
  isVideo?: boolean;
  store: any;
  elementId: string;
  onSave: (cropData: { x: number; y: number; width: number; height: number }) => void;
  onCancel: () => void;
}

const CropMedia: React.FC<CropMediaProps> = ({
  mediaUrl,
  isVideo = false,
  store,
  elementId,
  onSave,
  onCancel
}) => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [cropper, setCropper] = useState<any>(null);
  const [videoFrame, setVideoFrame] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement>(null);

  useEffect(() => {
    if (isVideo && videoFrame === null) {
      setIsLoading(true);
      const video = document.createElement('video');
      video.src = mediaUrl;
      video.crossOrigin = "anonymous";
      video.muted = true;
      
      const captureFrame = () => {
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          console.error('Video dimensions not available');
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
          const frameData = canvas.toDataURL('image/jpeg', 0.95);
            setVideoFrame(frameData);
          setIsLoading(false);
        }
      };

      const handleCanPlay = () => {
        video.currentTime = 0;
        // Capture frame after a short delay to ensure video is ready
        setTimeout(captureFrame, 100);
      };

      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', (e) => {
        console.error('Error loading video:', e);
        message.error('Failed to load video frame');
        setIsLoading(false);
      });

      video.load();
      video.play().catch(console.error);

      return () => {
        video.removeEventListener('canplay', handleCanPlay);
        video.pause();
        video.src = '';
      };
    } else {
      setIsLoading(false);
    }
  }, [mediaUrl, isVideo]);
  const handleSave = () => {
    if (cropper) {
      const cropData = cropper.getData();
      const element = store.getElementById(elementId);
      if (element) {
        // Convert coordinates to match Polotno's coordinate system
        const imageWidth = cropper.getImageData().width;
        const imageHeight = cropper.getImageData().height;
        
        // Calculate relative coordinates
        const relativeX = cropData.x / imageWidth;
        const relativeY = cropData.y / imageHeight;
        const relativeWidth = cropData.width / imageWidth;
        const relativeHeight = cropData.height / imageHeight;

        element.set({
          cropX: relativeX,
          cropY: relativeY,
          cropWidth: relativeWidth,
          cropHeight: relativeHeight
        });

        onSave({
          x: relativeX,
          y: relativeY,
          width: relativeWidth,
          height: relativeHeight
        });
        setIsModalOpen(false);
      }
    } else {
      message.error('Please crop the media first');
    }
  };

  const getInitialCropData = () => {
    const element = store.getElementById(elementId);
    if (element) {
      // Convert from relative coordinates back to absolute coordinates
      const imageWidth = element.width || 800; // Default width if not set
      const imageHeight = element.height || 600; // Default height if not set

      return {
        x: (element.cropX || 0) * imageWidth,
        y: (element.cropY || 0) * imageHeight,
        width: (element.cropWidth || 1) * imageWidth,
        height: (element.cropHeight || 1) * imageHeight
      };
    }
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    };
  };

  return (
    <Modal
      title="Crop Media"
      open={isModalOpen}
      onCancel={() => {
        setIsModalOpen(false);
        onCancel();
      }}
      footer={[
        <Button key="cancel" onClick={() => {
          setIsModalOpen(false);
          onCancel();
        }}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save
        </Button>
      ]}
      width={800}
      centered
    >
      <div className="crop-container">
        {isLoading ? (
          <div className="loading-container">
            <Spin size="large" />
            <div className="loading-text">Loading media...</div>
          </div>
        ) : isVideo ? (
          videoFrame ? (
            <Cropper
              src={videoFrame}
              style={{ height: 400, width: '100%' }}
              guides={true}
              data={getInitialCropData()}
              onInitialized={(instance) => setCropper(instance)}
              dragMode="move"
              autoCropArea={1}
              restore={false}
              modal={true}
              highlight={true}
              cropBoxMovable={true}
              cropBoxResizable={true}
              toggleDragModeOnDblclick={true}
              minCropBoxWidth={10}
              minCropBoxHeight={10}
            />
          ) : (
            <div className="loading">Failed to load video frame</div>
          )
        ) : (
          <Cropper
            src={mediaUrl}
            style={{ height: 400, width: '100%' }}
            guides={true}
            data={getInitialCropData()}
            onInitialized={(instance) => setCropper(instance)}
            dragMode="move"
            autoCropArea={1}
            restore={false}
            modal={true}
            highlight={true}
            cropBoxMovable={true}
            cropBoxResizable={true}
            toggleDragModeOnDblclick={true}
            minCropBoxWidth={10}
            minCropBoxHeight={10}
          />
        )}
      </div>
    </Modal>
  );
};

export default CropMedia;


